import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PlaceIcon from '@mui/icons-material/Place';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import { Box, Button, Chip, MenuItem, Paper, TextField, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { RootState } from '../redux/store';
import { GlowAlert } from '../shared/components/GlowAlert';
import { LoadingButton } from '../shared/components/LoadingButton';
import { Section } from '../shared/components/Section';
import { UploadDetailDialog } from '../uploads/components/UploadDetailDialog';
import { useSelectedUser } from '../users/hooks/useSelectedUser';
import { useUploadImage } from '../uploads/useUploadImage';
import {
    AdminUpload,
    errorMessage,
    formatBytes,
    formatTakenAt,
    getUploadsSummary,
    isImage,
    kindLabel,
    listUploads,
    STATUS_LABELS,
    UPLOAD_KINDS,
    UPLOAD_STATUSES,
    UploadsSummary,
} from '../uploads/uploadsApi';

const PAGE_SIZE = 50;
const SEARCH_DELAY_MS = 300;

const Thumbnail: React.FC<{ upload: AdminUpload }> = ({ upload }) => {
    const image = isImage(upload.mime_type);
    const url = useUploadImage(upload.id, image);
    return url ? (
        <Box component="img" src={url} alt="" sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }} />
    ) : (
        <Box
            sx={{
                width: 56, height: 56, borderRadius: 1, flexShrink: 0, display: 'flex', alignItems: 'center',
                justifyContent: 'center', backgroundColor: 'action.hover', color: 'text.secondary',
            }}
        >
            <DescriptionOutlinedIcon />
        </Box>
    );
};

const Tile: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <Paper variant="outlined" sx={{ px: 2, py: 1, minWidth: 120 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {label}
        </Typography>
        <Typography variant="h6">{value}</Typography>
    </Paper>
);

/** Every user's uploads: what was sent, what became of it, and what was read from it. */
const UploadsPage: React.FC = () => {
    const users = useSelector((state: RootState) => state.users.users);
    const [uploads, setUploads] = useState<AdminUpload[]>([]);
    const [nextCursor, setNextCursor] = useState<string | null>(null);
    const [summary, setSummary] = useState<UploadsSummary | null>(null);
    const [userId, setUserId] = useSelectedUser();
    const [status, setStatus] = useState('');
    const [kind, setKind] = useState('');
    const [search, setSearch] = useState('');
    const [query, setQuery] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // The upload whose details are open is in the address (/uploads/<id>): it can be linked to and reloaded.
    const { uploadId: openId = null } = useParams<{ uploadId?: string }>();
    const navigate = useNavigate();
    const setOpenId = useCallback(
        (id: string | null) => navigate(id ? `/uploads/${encodeURIComponent(id)}` : '/uploads'),
        [navigate]
    );

    // Typing searches after a pause, not on every key.
    useEffect(() => {
        const timer = setTimeout(() => setQuery(search.trim()), SEARCH_DELAY_MS);
        return () => clearTimeout(timer);
    }, [search]);

    const load = useCallback(
        async (cursor?: string) => {
            setLoading(true);
            setError(null);
            try {
                const page = await listUploads(
                    { user_id: userId || undefined, status: status || undefined, type: kind || undefined, q: query || undefined },
                    cursor,
                    PAGE_SIZE
                );
                setUploads((previous) => (cursor ? [...previous, ...page.items] : page.items));
                setNextCursor(page.next_cursor);
            } catch (err) {
                setError(errorMessage(err, 'Failed to load uploads'));
            } finally {
                setLoading(false);
            }
        },
        [userId, status, kind, query]
    );

    const loadSummary = useCallback(async () => {
        try {
            setSummary(await getUploadsSummary(userId || undefined));
        } catch {
            setSummary(null);
        }
    }, [userId]);

    useEffect(() => {
        setUploads([]);
        setNextCursor(null);
        load();
    }, [load]);

    useEffect(() => {
        loadSummary();
    }, [loadSummary]);

    const refresh = () => {
        setUploads([]);
        load();
        loadSummary();
    };

    const changed = ({ deleted }: { deleted: boolean }) => {
        // A rename or a re-read changes one row; a delete changes the counts too.
        setUploads([]);
        load();
        if (deleted) loadSummary();
    };

    return (
        <Section title="Uploads" onRefresh={refresh} refreshing={loading} subtitle="Every file users have sent, what became of it, and what was read from it.">
            {summary && (
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                    <Tile label="Files" value={summary.total} />
                    <Tile label="Storage" value={formatBytes(summary.total_bytes)} />
                    <Tile label="With EXIF" value={summary.with_metadata} />
                    <Tile label="With a location" value={summary.with_gps} />
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                        {UPLOAD_STATUSES.filter((s) => summary.by_status[s].count > 0).map((s) => (
                            <Chip
                                key={s}
                                size="small"
                                color={status === s ? 'primary' : s === 'failed' ? 'error' : 'default'}
                                variant={status === s ? 'filled' : 'outlined'}
                                label={`${STATUS_LABELS[s]} ${summary.by_status[s].count}`}
                                onClick={() => setStatus(status === s ? '' : s)}
                            />
                        ))}
                    </Box>
                </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
                <TextField select size="small" label="User" value={userId} onChange={(e) => setUserId(e.target.value)} sx={{ minWidth: 240 }}>
                    <MenuItem value="">All users</MenuItem>
                    {users.map((u) => (
                        <MenuItem key={u.id} value={u.id}>
                            {u.display_name || u.email || u.id}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 150 }}>
                    <MenuItem value="">All</MenuItem>
                    {UPLOAD_STATUSES.map((s) => (
                        <MenuItem key={s} value={s}>
                            {STATUS_LABELS[s]}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField select size="small" label="Type" value={kind} onChange={(e) => setKind(e.target.value)} sx={{ minWidth: 190 }}>
                    <MenuItem value="">All</MenuItem>
                    {UPLOAD_KINDS.map((k) => (
                        <MenuItem key={k.value} value={k.value}>
                            {k.label}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    size="small"
                    label="Search names"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ minWidth: 220 }}
                />
            </Box>

            {error && <GlowAlert sx={{ mb: 2 }}>{error}</GlowAlert>}

            {uploads.map((u) => (
                <Paper
                    key={u.id}
                    variant="outlined"
                    role="button"
                    tabIndex={0}
                    aria-label={`Open ${u.name}`}
                    onClick={() => setOpenId(u.id)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setOpenId(u.id);
                        }
                    }}
                    sx={{
                        p: 1.25, mb: 1, display: 'flex', gap: 1.5, alignItems: 'center', cursor: 'pointer',
                        backgroundColor: 'background.paper', '&:hover': { borderColor: 'primary.main' },
                    }}
                >
                    <Thumbnail upload={u} />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            <Typography variant="subtitle2" sx={{ wordBreak: 'break-word' }}>
                                {u.name}
                            </Typography>
                            <Chip size="small" variant="outlined" label={kindLabel(u.mime_type)} />
                            <Chip size="small" variant="outlined" label={formatBytes(u.size_bytes)} />
                            <Chip
                                size="small"
                                color={u.status === 'failed' ? 'error' : 'default'}
                                label={STATUS_LABELS[u.status]}
                            />
                            {u.taken_at && <Chip size="small" variant="outlined" label={`Taken ${formatTakenAt(u.taken_at).slice(0, 16)}`} />}
                            {u.camera && <Chip size="small" variant="outlined" icon={<PhotoCameraOutlinedIcon />} label={u.camera} />}
                            {u.has_gps && <Chip size="small" variant="outlined" icon={<PlaceIcon />} label="Location" />}
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
                            {u.user_name} · {new Date(u.created_at).toLocaleString()}
                            {u.original_filename !== u.name ? ` · ${u.original_filename}` : ''}
                        </Typography>
                    </Box>
                </Paper>
            ))}

            {!loading && !error && uploads.length === 0 && <Typography color="text.secondary">No uploads found.</Typography>}
            {loading && uploads.length === 0 && <GlowAlert severity="info">Loading uploads...</GlowAlert>}
            {nextCursor && (
                <LoadingButton variant="outlined" loading={loading} onClick={() => load(nextCursor)}>
                    Load older uploads
                </LoadingButton>
            )}

            {openId && <UploadDetailDialog uploadId={openId} onClose={() => setOpenId(null)} onChanged={changed} />}
        </Section>
    );
};

export default UploadsPage;
